import { useState } from "react";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { HiExclamation } from "react-icons/hi";
import Nango from "@nangohq/frontend";

import { StatusDot } from "../../../../components";
import { useIntegrations } from "../../../../contexts/IntegrationsContext";
import { useAuth, useToast } from "../../../../hooks";
import { NOTIFICATION_TYPES } from "../../../../utils";
import { DisconnectConfirmation } from "../DisconnectConfirmation";

export const MixpanelHeader = ({ isConnected, integration, credentials }) => {
  const { teamId, userId, user, apiClient, getOrCreateNangoSessionToken } =
    useAuth();
  const { connectIntegration, disconnectIntegration, fetchAllIntegrations } =
    useIntegrations();
  const showToast = useToast();

  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [error, setError] = useState(null);

  const getTeamName = async () => {
    try {
      const res = await apiClient.get("/teams/");
      return res.data.results[0].name;
    } catch (err) {
      console.error("Failed to fetch teams:", err);
      throw new Error("Failed to fetch team information");
    }
  };

  // Validate credentials before connecting
  const validateCredentials = () => {
    if (!credentials?.project_id?.trim()) {
      setError("Project ID is required");
      return false;
    }
    if (!credentials?.service_account_username?.trim()) {
      setError("Service Account Username is required");
      return false;
    }
    if (!credentials?.service_account_password?.trim()) {
      setError("Service Account Password is required");
      return false;
    }
    return true;
  };

  const handleConnect = async () => {
    // Validate credentials first
    if (!validateCredentials()) {
      return;
    }

    setConnectLoading(true);
    setError(null);

    try {
      const teamName = await getTeamName();
      const nangoConnectSessionToken = await getOrCreateNangoSessionToken(
        userId,
        user.email,
      );

      const nango = new Nango({
        connectSessionToken: nangoConnectSessionToken,
      });

      const nangoTeamId = `${teamName}-${teamId}`;

      // Pass credentials for Basic Auth (service account username/password)
      const intResult = await nango.auth("mixpanel", teamId, {
        credentials: {
          username: credentials.service_account_username,
          password: credentials.service_account_password,
        },
      });

      const integrationData = {
        name: `${userId}:${nangoTeamId}`,
        connection_id: intResult.connectionId,
        integration_id: "mixpanel",
        team: teamId,
        // Store credentials in config for later use
        config: {
          project_id: credentials.project_id,
          service_account_username: credentials.service_account_username,
          service_account_password: credentials.service_account_password,
        },
      };

      await connectIntegration(integrationData);
      await fetchAllIntegrations();

      showToast({
        content: "Mixpanel connected successfully!",
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      console.error("Failed to connect Mixpanel:", err);
      const errorMsg =
        err.response?.data?.error ||
        err.message ||
        "Failed to connect Mixpanel. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    setError(null);

    try {
      await disconnectIntegration(integration.id);
      setShowDisconnectModal(false);
      showToast({
        content: "Mixpanel disconnected successfully!",
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      console.error("Failed to disconnect Mixpanel:", err);
      const errorMsg =
        err.response?.data?.error ||
        "Failed to disconnect Mixpanel. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setDisconnectLoading(false);
    }
  };

  const isButtonLoading = connectLoading || disconnectLoading;

  return (
    <>
      {error && (
        <div className="mt-4 px-6">
          <div className="rounded-md bg-red-50 p-3">
            <div className="flex">
              <HiExclamation className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-row items-center justify-between px-6">
        <div className="flex flex-row items-center justify-center gap-2.5">
          <span className="mr-2 text-xl font-bold text-purple-600">
            Mixpanel
          </span>
          {isButtonLoading ? (
            <AiOutlineLoading className="h-6 w-6 animate-spin" />
          ) : (
            <StatusDot status={isConnected ? "green" : undefined} />
          )}
        </div>

        <div className="flex flex-row items-center justify-between gap-2">
          <Button
            color={isConnected ? "gray" : "blue"}
            className="w-36"
            disabled={isButtonLoading}
            isProcessing={isButtonLoading}
            onClick={
              isConnected ? () => setShowDisconnectModal(true) : handleConnect
            }
          >
            {isConnected ? "Disconnect" : "Connect"}
          </Button>
        </div>
      </div>

      <DisconnectConfirmation
        show={showDisconnectModal}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectModal(false)}
        integrationName="Mixpanel"
      />
    </>
  );
};
